import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { planService } from '../services/api';
import { Plan } from '../types';
import {
  Check,
  ArrowLeft,
  Building2,
  User,
  MapPin,
  Shield
} from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
`;

const Header = styled.header`
  background: white;
  padding: 1rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #00d4aa;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #00d4aa;
  }
`;

const Content = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FormSection = styled.section`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const Select = styled.select<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const Textarea = styled.textarea<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.875rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: transparent;
    color: #00d4aa;
    border: 2px solid #00d4aa;

    &:hover {
      background: #00d4aa;
      color: white;
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Summary = styled.aside``;

const SummaryCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 2rem;
`;

const PlanName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #00d4aa;
  margin-bottom: 1.5rem;
`;

const FeaturesList = styled.ul`
  list-style: none;
  margin-bottom: 2rem;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  color: #666;
  font-size: 0.875rem;
`;

const CheckIcon = styled(Check)`
  color: #28a745;
  flex-shrink: 0;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: #e9ecef;
  margin: 1.5rem 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 1.1rem;
  color: #333;
`;

const SecurityNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #666;
`;

interface ContractForm {
  companyName: string;
  cnpj: string;
  ownerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  businessType: string;
  notes: string;
}

const BRAZIL_STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapa' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceara' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espirito Santo' },
  { value: 'GO', label: 'Goias' },
  { value: 'MA', label: 'Maranhao' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Para' },
  { value: 'PB', label: 'Paraiba' },
  { value: 'PR', label: 'Parana' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piaui' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondonia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'Sao Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

export const ContractPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const { planName } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [planError, setPlanError] = useState<string | null>(null);
  const [cepLookupError, setCepLookupError] = useState<string | null>(null);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ContractForm>();

  const normalize = (value: string) => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setIsLoadingPlan(true);
        const response = await planService.getPlans();
        const data = response.data || [];
        setPlans(data);
      } catch (error) {
        console.error('Erro ao carregar plano da contratação:', error);
        setPlanError('Não foi possível carregar os detalhes do plano.');
      } finally {
        setIsLoadingPlan(false);
      }
    };

    loadPlans();
  }, []);

  const zipCodeValue = watch('zipCode');

  useEffect(() => {
    const digits = (zipCodeValue || '').replace(/\D/g, '');
    if (digits.length !== 8) {
      setCepLookupError(null);
      return;
    }

    const lookupCep = async () => {
      try {
        setCepLookupError(null);
        const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
        const data = await response.json();

        if (data.erro) {
          setCepLookupError('CEP nao encontrado.');
          return;
        }

        const fullStreet = [data.logradouro, data.bairro].filter(Boolean).join(', ');
        if (fullStreet) {
          setValue('address', fullStreet, { shouldValidate: true, shouldDirty: true });
        }

        if (data.localidade) {
          setValue('city', data.localidade, { shouldValidate: true, shouldDirty: true });
        }

        if (data.uf) {
          setValue('state', data.uf, { shouldValidate: true, shouldDirty: true });
        }
      } catch (error) {
        console.error('Erro ao consultar CEP:', error);
        setCepLookupError('Nao foi possivel consultar o CEP agora.');
      }
    };

    lookupCep();
  }, [zipCodeValue, setValue]);

  const currentPlan = useMemo(() => {
    if (!plans.length) {
      return null;
    }

    const routeSlug = normalize(planName || '');
    return plans.find(plan => {
      const planSlug = plan.slug ? normalize(plan.slug) : '';
      const nameSlug = normalize(plan.name || '');
      return routeSlug === planSlug || routeSlug === nameSlug;
    }) || null;
  }, [planName, plans]);

  const formatPrice = (price: number) => {
    if (price === 0) {
      return 'Grátis';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const currentPlanFeatures = useMemo(() => {
    if (!currentPlan) {
      return [];
    }

    const baseFeatures = [
      currentPlan.max_stores === -1 ? 'Lojas ilimitadas' : `Até ${currentPlan.max_stores} lojas`,
      currentPlan.max_employees_per_store === -1
        ? 'Funcionários ilimitados por loja'
        : `Até ${currentPlan.max_employees_per_store} funcionários por loja`,
      currentPlan.max_customers === -1 ? 'Clientes ilimitados' : `Até ${currentPlan.max_customers.toLocaleString('pt-BR')} clientes`,
      currentPlan.max_rewards === -1 ? 'Recompensas ilimitadas' : `Até ${currentPlan.max_rewards} recompensas`,
    ];

    const dynamicFeatures = [
      ...(currentPlan.features || []),
      ...(currentPlan.has_advanced_reports ? ['Relatórios avançados'] : []),
      ...(currentPlan.has_api_access ? ['API personalizada'] : []),
      ...(currentPlan.has_crm ? ['CRM habilitado'] : []),
      ...(currentPlan.has_custom_smtp ? ['SMTP personalizado'] : []),
      ...(currentPlan.has_tier_program ? ['Programa de níveis (tiers)'] : []),
      ...(currentPlan.has_customer_automation ? ['Automações por comportamento do cliente'] : []),
      ...(currentPlan.has_partner_network ? ['Rede de parceiros para earn/burn'] : []),
      ...(currentPlan.has_ai_insights ? ['Insights com IA (churn/CLV/NBO)'] : []),
      ...(currentPlan.has_priority_support ? ['Suporte prioritário'] : []),
    ];

    return Array.from(new Set([...baseFeatures, ...dynamicFeatures]));
  }, [currentPlan]);

  const onSubmit = async (data: ContractForm) => {
    if (!currentPlan) {
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Implementar API para contratação de plano
      console.log('Dados do formulário:', data);
      console.log('Plano selecionado:', planName);

      // Simular envio
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Redirecionar para página de sucesso ou pagamento
      navigate('/contract-success', {
        state: {
          plan: currentPlan,
          customerData: data
        }
      });

    } catch (error) {
      console.error('Erro ao contratar plano:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Header>
        <Nav>
          <Logo>PontoFiel</Logo>
          <BackButton onClick={() => navigate('/')}>
            <ArrowLeft size={16} />
            Voltar
          </BackButton>
        </Nav>
      </Header>

      <Content>
        <div>
          <FormSection>
            <SectionTitle>
              <Building2 size={20} />
              Dados da Empresa
            </SectionTitle>

            <Form onSubmit={handleSubmit(onSubmit)}>
              <InputRow>
                <InputGroup>
                  <Label>Nome da Empresa *</Label>
                  <Input
                    type="text"
                    placeholder="Ex: Minha Empresa Ltda"
                    $hasError={!!errors.companyName}
                    {...register('companyName', {
                      required: 'Nome da empresa é obrigatório'
                    })}
                  />
                  {errors.companyName && <ErrorMessage>{errors.companyName.message}</ErrorMessage>}
                </InputGroup>

                <InputGroup>
                  <Label>CNPJ *</Label>
                  <Input
                    type="text"
                    placeholder="00.000.000/0000-00"
                    $hasError={!!errors.cnpj}
                    {...register('cnpj', {
                      required: 'CNPJ é obrigatório',
                      pattern: {
                        value: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
                        message: 'CNPJ inválido'
                      }
                    })}
                  />
                  {errors.cnpj && <ErrorMessage>{errors.cnpj.message}</ErrorMessage>}
                </InputGroup>
              </InputRow>

              <InputRow>
                <InputGroup>
                  <Label>Nome do Responsável *</Label>
                  <Input
                    type="text"
                    placeholder="Nome completo"
                    $hasError={!!errors.ownerName}
                    {...register('ownerName', {
                      required: 'Nome do responsável é obrigatório'
                    })}
                  />
                  {errors.ownerName && <ErrorMessage>{errors.ownerName.message}</ErrorMessage>}
                </InputGroup>

                <InputGroup>
                  <Label>Ramo de Atividade *</Label>
                  <Select
                    $hasError={!!errors.businessType}
                    {...register('businessType', {
                      required: 'Ramo de atividade é obrigatório'
                    })}
                  >
                    <option value="">Selecione...</option>
                    <option value="varejo">Varejo</option>
                    <option value="alimentacao">Alimentação</option>
                    <option value="servicos">Serviços</option>
                    <option value="saude">Saúde e Beleza</option>
                    <option value="outros">Outros</option>
                  </Select>
                  {errors.businessType && <ErrorMessage>{errors.businessType.message}</ErrorMessage>}
                </InputGroup>
              </InputRow>

              <SectionTitle style={{ marginTop: '2rem' }}>
                <User size={20} />
                Dados de Contato
              </SectionTitle>

              <InputRow>
                <InputGroup>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="contato@empresa.com"
                    $hasError={!!errors.email}
                    {...register('email', {
                      required: 'Email é obrigatório',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Email inválido'
                      }
                    })}
                  />
                  {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
                </InputGroup>

                <InputGroup>
                  <Label>Telefone *</Label>
                  <Input
                    type="tel"
                    placeholder="(11) 99999-9999"
                    $hasError={!!errors.phone}
                    {...register('phone', {
                      required: 'Telefone é obrigatório'
                    })}
                  />
                  {errors.phone && <ErrorMessage>{errors.phone.message}</ErrorMessage>}
                </InputGroup>
              </InputRow>

              <SectionTitle style={{ marginTop: '2rem' }}>
                <MapPin size={20} />
                Endereço
              </SectionTitle>

              <InputGroup>
                <Label>Endereço Completo *</Label>
                <Input
                  type="text"
                  placeholder="Rua, número, bairro"
                  $hasError={!!errors.address}
                  {...register('address', {
                    required: 'Endereço é obrigatório'
                  })}
                />
                {errors.address && <ErrorMessage>{errors.address.message}</ErrorMessage>}
              </InputGroup>

              <InputRow>
                <InputGroup>
                  <Label>Cidade *</Label>
                  <Input
                    type="text"
                    placeholder="São Paulo"
                    $hasError={!!errors.city}
                    {...register('city', {
                      required: 'Cidade é obrigatória'
                    })}
                  />
                  {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
                </InputGroup>

                <InputGroup>
                  <Label>Estado *</Label>
                  <Select
                    $hasError={!!errors.state}
                    {...register('state', {
                      required: 'Estado é obrigatório'
                    })}
                  >
                    <option value="">Selecione...</option>
                    {BRAZIL_STATES.map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </Select>
                  {errors.state && <ErrorMessage>{errors.state.message}</ErrorMessage>}
                </InputGroup>
              </InputRow>

              <InputGroup>
                <Label>CEP</Label>
                <Input
                  type="text"
                  placeholder="00000-000"
                  maxLength={9}
                  {...register('zipCode')}
                />
                {cepLookupError && <ErrorMessage>{cepLookupError}</ErrorMessage>}
              </InputGroup>

              <InputGroup>
                <Label>Observações</Label>
                <Textarea
                  placeholder="Informações adicionais sobre sua empresa ou necessidades específicas..."
                  {...register('notes')}
                />
              </InputGroup>

              <Button
                type="submit"
                $variant="primary"
                disabled={isSubmitting || isLoadingPlan || !currentPlan}
                style={{ marginTop: '1rem' }}
              >
                {isSubmitting ? 'Processando...' : 'Contratar Plano'}
              </Button>
            </Form>
          </FormSection>
        </div>

        <Summary>
          <SummaryCard>
            {isLoadingPlan && <PlanName>Carregando plano...</PlanName>}
            {!isLoadingPlan && planError && <PlanName>{planError}</PlanName>}
            {!isLoadingPlan && !planError && !currentPlan && (
              <PlanName>Plano não encontrado para este link.</PlanName>
            )}
            {!isLoadingPlan && !planError && currentPlan && (
              <>
                <PlanName>{currentPlan.name}</PlanName>
                <PlanPrice>
                  {formatPrice(currentPlan.price)}
                  {currentPlan.price > 0 && <span style={{ fontSize: '1rem', color: '#666' }}>/mês</span>}
                </PlanPrice>

                <FeaturesList>
                  {currentPlanFeatures.map((feature, index) => (
                    <Feature key={index}>
                      <CheckIcon size={16} />
                      {feature}
                    </Feature>
                  ))}
                </FeaturesList>

                <Divider />

                <TotalRow>
                  <span>Total mensal:</span>
                  <span>{formatPrice(currentPlan.price)}</span>
                </TotalRow>
              </>
            )}

            <SecurityNote>
              <Shield size={16} />
              Seus dados estão seguros e protegidos
            </SecurityNote>
          </SummaryCard>
        </Summary>
      </Content>
    </Container>
  );
};
